import React, { useEffect, useRef, useState } from "react";
import { ShoppingCart } from "lucide-react";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { FiShoppingCart } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Usermenu from "./Usermenu";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();


  const isOnLayoutPage = location.pathname.startsWith("/Layout");

  const userFromStore = useSelector((state) => state?.user?.data ?? state?.user);
  const isLoggedIn = Boolean(userFromStore && userFromStore._id);

  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close user menu on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenUserMenu(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Close user menu when navigating
  useEffect(() => {
    setOpenUserMenu(false);
    setIsMenuOpen(false); // Also close mobile menu on navigation
  }, [location.pathname]);

  const handleCartOpen = (e) => {
    e.preventDefault();
    console.log("Cart icon clicked");
    setIsMenuOpen(false);
    // Prevent default behavior
    e.stopPropagation();
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    navigate("/login");
  };

  // const handleLinkClick = (e) => {
  //   // Prevent any default scrolling behavior
  //   e.preventDefault();
  //   setIsMenuOpen(false);
    
  //   // Get the target path from the link
  //   const targetPath = e.currentTarget.getAttribute('href');
  //   if (targetPath) {
  //     navigate(targetPath);
  //   }
  // };

  // const handleStyleSelectClick = (e) => {
  //   e.preventDefault();
  //   setIsMenuOpen(false);
  //   navigate("/catalog");
  // };

  // // Function to handle smooth scroll prevention
  // const preventDefaultScroll = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  // };

  return (
    <header className="bg-white shadow-md py-3 px-4 md:px-8 sticky top-0 z-50">
      <div className="mx-auto flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <Link 
            to="/" 
            className="flex items-center space-x-2"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
              setIsMenuOpen(false);
            }}
          >
            <img
              src="/assets/Images/logoimg.png"
              alt="Laminance logo"
              className="h-10 w-auto"
            />
          </Link>
        </div>

        {/* Desktop Nav Links */}
        <nav className="hidden md:flex space-x-6">
          <button
            onClick={() => {
              navigate("/");
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className="text-gray-700 hover:text-orange-500 bg-transparent border-none cursor-pointer"
          >
            Home
          </button>

          <button
            onClick={() => {
              navigate("/about");
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className="text-gray-700 hover:text-orange-500 bg-transparent border-none cursor-pointer"
          >
            About
          </button>

          <button
            onClick={() => {
              navigate("/contact");
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className="text-gray-700 hover:text-orange-500 bg-transparent border-none cursor-pointer"
          >
            Contact
          </button>
          <button
            onClick={() => {
              navigate("/gallery");
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className="text-gray-700 hover:text-orange-500 bg-transparent border-none cursor-pointer"
          >
            Gallery
          </button>
        </nav>

        {/* Right Side */}
        <div className="flex items-center space-x-4">
          {/* Select Style / Cart (depends on Layout page) */}
          {!isOnLayoutPage ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                navigate("/catalog");
                window.scrollTo({ top: 0, behavior: 'instant' });
              }}
              className="hidden md:block bg-[#30465a] text-white px-4 py-2 rounded hover:bg-[#243a4a] border-none cursor-pointer"
            >
              Select Your Style
            </button>
          ) : (
            <button
              onClick={handleCartOpen}
              className="hidden md:flex items-center gap-2 border border-orange-500 text-orange-500 px-3 py-2 rounded hover:bg-orange-50 cursor-pointer"
            >
              <FiShoppingCart size={20} />
              <span className="hidden lg:inline">Cart</span>
            </button>
          )}

          {/* Cart Icon (always visible) */}
          <button 
            className="relative md:hidden border-none bg-transparent" 
            aria-label="Cart"
            onClick={handleCartOpen}
          >
            <ShoppingCart className="w-6 h-6 text-gray-600" />
            <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full px-1">
              3
            </span>
          </button>

          {/* Account/Login */}
          {isLoggedIn ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenUserMenu((s) => !s);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition cursor-pointer border-none"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
              >
                <span>Account</span>
                {openUserMenu ? <GoTriangleUp /> : <GoTriangleDown />}
              </button>

              {openUserMenu && (
                <div 
                  className="absolute right-0 mt-2 bg-white rounded-xl shadow-lg p-4 min-w-52 z-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Usermenu />
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={handleLoginClick}
              className="bg-[#284563] hover:bg-[#1e2f3b] text-white px-4 py-2 rounded transition border-none cursor-pointer"
            >
              Login
            </button>
          )}

          {/* Mobile Hamburger */}
          <div className="md:hidden">
            <button
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(!isMenuOpen);
              }}
              className="text-orange-500 border-none bg-transparent"
              aria-label="Toggle Navigation Menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 5.25h16.5m-16.5 6h16.5m-16.5 6h16.5"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <nav 
          className="md:hidden bg-white shadow-md mt-4 px-4 py-3 space-y-4 rounded-md"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              navigate("/");
              setIsMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className="block text-gray-700 hover:text-orange-500 w-full text-left bg-transparent border-none cursor-pointer"
          >
            Home
          </button>
          <button
            onClick={() => {
              navigate("/contact");
              setIsMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className="block text-gray-700 hover:text-orange-500 w-full text-left bg-transparent border-none cursor-pointer"
          >
            Contact
          </button>
          <button
            onClick={() => {
              navigate("/gallery");
              setIsMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'instant' });
            }}
            className="block text-gray-700 hover:text-orange-500 w-full text-left bg-transparent border-none cursor-pointer"
          >
            Gallery
          </button>

          {!isOnLayoutPage ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                navigate("/catalog");
                setIsMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'instant' });
              }}
              className="w-full bg-[#30465a] text-white px-4 py-2 rounded hover:bg-[#243a4a] text-center border-none cursor-pointer"
            >
              Select Your Style
            </button>
          ) : (
            <button
              onClick={handleCartOpen}
              className="w-full flex justify-center items-center gap-2 bg-[#30465a] text-white px-4 py-2 rounded hover:bg-[#243a4a] text-center border-none cursor-pointer"
            >
              <FiShoppingCart size={20} />
              <span>Cart</span>
            </button>
          )}
        </nav>
      )}
    </header>
  );
}